// Makes an AG Grid filter popup body (a plain DOM element built by our custom
// IFilterComp implementations) user-resizable via a triangle handle in the
// bottom-right corner. Chosen dimensions are persisted to localStorage so they
// survive across sessions. localStorage is per browser profile, so this is
// naturally "per user". Pass a `storageKey` that includes the column id so each
// filter remembers its own size independently.
//
// Constraints (product requirement):
//   - max width  = 50%  of the table width
//   - max height = 100% of the table height
// Both are recomputed from the live grid element at drag time so they track
// window resizes.

// Fallback only — callers should pass a per-column `storageKey`.
const DEFAULT_STORAGE_KEY = 'westbury.masterDiscography.filterBoxSize';
const DEFAULT_WIDTH = 250;
const MIN_WIDTH = 200;
const MIN_HEIGHT = 140;

interface SavedSize {
  width: number;
  height: number;
}

interface FilterResizeOptions {
  // Inner scrollable element (e.g. the option list) that should grow to fill
  // the box once an explicit height is applied. When no height is set the
  // element keeps its own natural/max-height so small filters stay compact.
  fillElement?: HTMLElement;
  storageKey?: string;
  minWidth?: number;
  minHeight?: number;
  defaultWidth?: number;
}

function readSavedSize(storageKey: string): SavedSize | null {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed.width === 'number' &&
      typeof parsed.height === 'number'
    ) {
      return parsed;
    }
  } catch {
    // Ignore malformed / unavailable storage.
  }
  return null;
}

function writeSavedSize(storageKey: string, size: SavedSize): void {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(size));
  } catch {
    // Ignore quota / unavailable storage.
  }
}

// Derive the resize limits from the live grid element. The filter popup is a
// descendant of `.ag-root-wrapper` (no custom popupParent is configured), so we
// can measure the table by walking up from the popup body.
function getMaxBounds(gui: HTMLElement): { maxWidth: number; maxHeight: number } {
  const grid = gui.closest('.ag-root-wrapper') as HTMLElement | null;
  const rect = grid?.getBoundingClientRect();
  const tableWidth = rect?.width ?? window.innerWidth;
  const tableHeight = rect?.height ?? window.innerHeight;
  return {
    maxWidth: Math.round(tableWidth * 0.5),
    maxHeight: Math.round(tableHeight),
  };
}

function clamp(value: number, min: number, max: number): number {
  // max can dip below min on very small tables; keep min as the floor.
  return Math.max(min, Math.min(value, Math.max(min, max)));
}

/**
 * Attaches a resize handle to `gui` and wires up drag + persistence.
 *
 * Call this once from the filter's `init()`. It returns an `applySavedSize`
 * function that should be called from `afterGuiAttached()` (once the popup is
 * in the DOM and the table can be measured) to restore and re-clamp the saved
 * dimensions.
 */
export function makeFilterBoxResizable(
  gui: HTMLElement,
  options: FilterResizeOptions = {}
): () => void {
  const {
    fillElement,
    storageKey = DEFAULT_STORAGE_KEY,
    minWidth = MIN_WIDTH,
    minHeight = MIN_HEIGHT,
    defaultWidth = DEFAULT_WIDTH,
  } = options;

  gui.style.position = 'relative';
  gui.style.boxSizing = 'border-box';
  gui.style.overflow = 'hidden';
  gui.style.maxWidth = 'none';
  gui.style.width = `${defaultWidth}px`;

  // Applying an explicit height turns the fill element into the flexible,
  // scrollable region so the box grows downward from the handle.
  const applyHeight = (height: number) => {
    gui.style.height = `${height}px`;
    if (fillElement) {
      fillElement.style.flex = '1 1 auto';
      fillElement.style.minHeight = '0';
      fillElement.style.maxHeight = 'none';
      fillElement.style.overflow = 'auto';
    }
  };

  const handle = document.createElement('div');
  handle.className = 'ag-filter-resize-handle';
  handle.setAttribute('aria-hidden', 'true');
  Object.assign(handle.style, {
    position: 'absolute',
    right: '0',
    bottom: '0',
    width: '16px',
    height: '16px',
    cursor: 'nwse-resize',
    // Bottom-right triangle.
    background: 'linear-gradient(135deg, transparent 50%, #6b7280 50%)',
    zIndex: '5',
    touchAction: 'none',
  } as Partial<CSSStyleDeclaration>);

  handle.addEventListener('pointerdown', (event) => {
    // Prevent text selection and keep AG Grid from treating the drag as an
    // outside click that would close the popup.
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const startY = event.clientY;
    const startWidth = gui.offsetWidth;
    const startHeight = gui.offsetHeight;
    const { maxWidth, maxHeight } = getMaxBounds(gui);

    try {
      handle.setPointerCapture(event.pointerId);
    } catch {
      // setPointerCapture can throw if the pointer is already released.
    }

    const onMove = (moveEvent: PointerEvent) => {
      const width = clamp(
        startWidth + (moveEvent.clientX - startX),
        minWidth,
        maxWidth
      );
      const height = clamp(
        startHeight + (moveEvent.clientY - startY),
        minHeight,
        maxHeight
      );
      gui.style.width = `${width}px`;
      applyHeight(height);
    };

    const onUp = () => {
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onUp);
      writeSavedSize(storageKey, {
        width: gui.offsetWidth,
        height: gui.offsetHeight,
      });
    };

    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp);
  });

  gui.appendChild(handle);

  return function applySavedSize() {
    const { maxWidth, maxHeight } = getMaxBounds(gui);
    const saved = readSavedSize(storageKey);

    const width = clamp(saved?.width ?? defaultWidth, minWidth, maxWidth);
    gui.style.width = `${width}px`;

    // Only pin a height once the user has resized before; otherwise keep the
    // natural (compact) height driven by content.
    if (saved?.height) {
      applyHeight(clamp(saved.height, minHeight, maxHeight));
    }
  };
}
